/**
 * Created by Rodrigo Quesada on 17/02/17.
 */
import {Injectable, Injector} from '@angular/core';
import {Routes, Router, Route, ActivatedRoute, ActivatedRouteSnapshot} from '@angular/router';
import {Observable} from 'rxjs';
import {AuthService} from '../../security/authorization/auth.service';

@Injectable()
export class NavigableNodeDeps {
    constructor(readonly router: Router, readonly injector: Injector, readonly authService: AuthService) {
    }
}

export abstract class NavigableNode<N extends NavigableNode<N, C>, C extends NavigableNode<C, any>> {

    abstract TYPE: NavigableNode.Type<N>;
    abstract CHILD_TYPE: NavigableNode.Type<C>;

    readonly path: string;
    parent?: NavigableNode<any, N>;

    private _previous?: N;
    private _next?: N;

    protected _currentChild?: C;

    private _pathToChildMap: {[path: string]: C} = {};
    private _childrenWithCustomPathMatching: C[] = [];

    readonly _router: Router;

    constructor(protected _nvnm: NavigableNodeDeps, route: Route, protected readonly _children: C[]) {
        this.path = route.path!;
        this._children.forEach(child => {
            child.parent = this;

            if (child.pathMatches !== undefined) this._childrenWithCustomPathMatching.push(child);
            else this._pathToChildMap[child.path] = child;
        });
    }

    init() { this._children.forEach(child => child.init()); }

    isLeaf(): boolean {
        return this._children.length === 0;
    }

    hasPreviousLeaf(): boolean {
        return this.hasPrevious() || this._currentChildHasPreviousLeaf();
    }

    protected _currentChildHasPreviousLeaf(): boolean {
        return !!this._currentChild && this._currentChild.hasPreviousLeaf();
    }

    hasNextLeaf(): boolean {
        return this.hasNext() || this._currentChildHasNextLeaf();
    }

    protected _currentChildHasNextLeaf(): boolean {
        return !!this._currentChild && this._currentChild.hasNextLeaf();
    }

    navigateToPreviousLeaf(): Observable<boolean> {
        if (this._currentChildHasPreviousLeaf()) {
            return this._navigateToCurrentChildPreviousLeaf();
        } else {
            return this._previousNonSkippedNode()!._navigateToLastLeaf();
        }
    }

    protected _navigateToCurrentChildPreviousLeaf(): Observable<boolean> {
        return this._currentChild!!.navigateToPreviousLeaf();
    }

    protected _navigateToLastLeaf(): Observable<boolean> {
        if (!this.isLeaf()) return this._findFirstNonSkipped(Stream.from(this._children).reverse())!._navigateToLastLeaf();
        else return this.navigate();
    }

    navigateToNextLeaf(): Observable<boolean> {
        if (this._currentChildHasNextLeaf()) {
            return this._navigateToCurrentChildNextLeaf();
        } else {
            return this._nextNonSkippedNode()!._navigateToFirstLeaf();
        }
    }

    protected _navigateToCurrentChildNextLeaf(): Observable<boolean> {
        return this._currentChild!!.navigateToNextLeaf();
    }

    protected _navigateToFirstLeaf(): Observable<boolean> {
        if (!this.isLeaf()) return this._findFirstNonSkipped(Stream.from(this._children))!._navigateToFirstLeaf();
        else return this.navigate();
    }

    hasPrevious(): boolean {
        //TODO optimize, an observable should be returned instead...
        return this._previousNonSkippedNode() !== undefined;
    }

    private _previousNonSkippedNode(): N|undefined {
        let node = this._previous;
        while (node !== undefined && node._skip()) {
            node = node._previous;
        }
        return node;
    }

    hasNext(): boolean {
        //TODO optimize, an observable should be returned instead...
        return this._nextNonSkippedNode() !== undefined;
    }

    private _nextNonSkippedNode(): N|undefined {
        let node = this._next;
        while (node !== undefined && node._skip()) {
            node = node._next;
        }
        return node;
    }

    navigate(): Observable<boolean> {
        return Observable.fromPromise(this._nvnm.router.navigateByUrl(this.pathFromRoot()));
    }

    protected _skip(): boolean {
        return this._findFirstNonSkipped(Stream.from(this._children)) === undefined;
    }

    pathFromRoot(): string {
        let pathToRoot: NavigableNode<any, any>[] = [];
        let currentNode: NavigableNode<any, any>|undefined = this;
        do {
            pathToRoot.push(currentNode);
            currentNode = currentNode.parent;
        } while (currentNode);

        return Stream.from(pathToRoot)
            .map(node => node.path)
            .filter(it => it.length > 0)
            .reverse()
            .join('/');
    }

    protected _setCurrentChild(child: C|undefined, route: ActivatedRoute) {
        this._currentChild = child;
    }

    refreshCurrentChild(route: ActivatedRoute) {
        let currentChild: C|undefined;

        if (!this.isLeaf()) {
            let pathAndRoute = NavigableNode._pathAndRoute(this.CHILD_TYPE, route);
            if (pathAndRoute) {
                let {path, route: childRoute} = pathAndRoute;
                currentChild = this._pathToChildMap[path];
                if (!currentChild) currentChild = this._childrenWithCustomPathMatching.find(c => c.pathMatches!!(path));

                if (currentChild) currentChild.refreshCurrentChild(childRoute);
            }
        }

        this._setCurrentChild(currentChild, route);
    }

    private static _pathAndRoute<N extends NavigableNode<N, any>>(type: NavigableNode.Type<N>, route: ActivatedRoute): {path: string, route: ActivatedRoute}|undefined {
        let associatedRoute = type._findAssociatedRoute(route);
        if (associatedRoute) return {
            path: routePath(associatedRoute),
            route: associatedRoute
        };
    }

    protected static _fromRoutes<N extends NavigableNode<any, any>>(navigableNodeDeps: NavigableNodeDeps, type: NavigableNode.Type<N>, routes: Routes): Observable<N> {
        let navigables$ = Observable.from(routes)
            .flatMap(route => type._fromRoute(navigableNodeDeps, route))
            .publishReplay();
        navigables$.connect();

        NavigableNode._setPreviousAndNext(navigables$);

        return navigables$;
    }

    private static _setPreviousAndNext(sections$: Observable<NavigableNode<any, any>>) {
        // TODO use and/when/then|forkJoin when implemented for RxJs 5?
        let sectionTriplets$ = sections$.concat(Observable.of(undefined))
            .scan((acc, value) => {
                acc.shift();
                acc.push(value);
                return acc;
            }, [undefined, undefined, undefined])
            .skip(1);

        sectionTriplets$.do(sectionTriplet => {
            let current = sectionTriplet[1]!;
            current._previous = sectionTriplet[0];
            current._next = sectionTriplet[2];
        }).subscribe();
    }

    pathMatches?: (path: string) => boolean;

    //region Utils
    private _findFirstNonSkipped(nodes: Stream<C>): C|undefined {
        const result = nodes.filter(n => !n._skip()).findFirst();
        return result.isPresent() ? result.get() : undefined;
    }

    //endregion
}

module NavigableNode {
    export interface Type<N extends NavigableNode<N, any>> {
        fromRoutes(navigableNodeDeps: NavigableNodeDeps, routes: Routes): Observable<N>;
        _fromRoute(navigableNodeDeps: NavigableNodeDeps, route: Route): Observable<N>;
        _findAssociatedRoute(route: ActivatedRoute): ActivatedRoute|undefined;
        _isAssociatedRoute(route: Route|ActivatedRouteSnapshot): boolean;
    }
}

// region Utils
// TODO Move code somewhere else
function routePath(route: ActivatedRoute): string {
    return route.snapshot.url.map(s => s.path).join('/');
}
// endregion



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./~/tslint-loader!./src/app/components/sections/service/navigable-node.class.ts